<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  openImageLightbox,
  openMediaLightbox,
  openVideoPreview,
  setImageDimensions,
} from '../../chatModules'
import { useAuthenticatedMediaUrl } from '@/modules/chat-matrix-fast/composables/useAuthenticatedMediaUrl'
import { useActiveChatMessages } from '@/modules/chat-matrix-fast/composables/useActiveChatMessages'
import { flattenChatMedia } from '@/modules/chat-matrix-fast/composables/useFilesAndMediaGallery'
import { resolveMediaUrlForDisplay } from '@/modules/chat-matrix-fast/services/matrixMediaUrl'

/** Фиксированная высота превью — резервируем место до загрузки, чтобы лента не прыгала. */
const IMAGE_FRAME_HEIGHT_PX = 200
const VIDEO_FRAME_HEIGHT_PX = 240

interface Props {
  src: string
  alt?: string
  kind: 'image' | 'video'
  /** Matrix `info.thumbnail_url` — JPEG first-frame poster for videos. */
  poster?: string | null
}

const props = defineProps<Props>()

const isVideo = computed(() => props.kind === 'video')
const activeMessages = useActiveChatMessages()

// Images: resolve on mount. Videos: full file only on open; poster loads separately.
const imageSrcRef = computed(() => (isVideo.value ? undefined : props.src))
const { resolvedSrc, isLoading, hasError, reload } = useAuthenticatedMediaUrl(
  imageSrcRef,
  { purpose: 'attachment' },
)

// Explicit uploaded JPEG poster (preferred).
const explicitPosterRef = computed(() => (
  isVideo.value && props.poster ? props.poster : undefined
))
const {
  resolvedSrc: explicitPosterSrc,
  isLoading: explicitPosterLoading,
} = useAuthenticatedMediaUrl(explicitPosterRef, { purpose: 'attachment' })

// Legacy videos without thumbnail_url: try Matrix /thumbnail/ (no full download).
const fallbackPosterRef = computed(() => (
  isVideo.value && !props.poster ? props.src : undefined
))
const {
  resolvedSrc: fallbackPosterSrc,
  isLoading: fallbackPosterLoading,
  hasError: fallbackPosterError,
} = useAuthenticatedMediaUrl(fallbackPosterRef, { purpose: 'poster' })

const posterSrc = computed(() => explicitPosterSrc.value || fallbackPosterSrc.value || '')
const posterLoading = computed(() => (
  Boolean(explicitPosterLoading.value || (fallbackPosterLoading.value && !posterSrc.value))
))

const mediaReloadAttempts = ref(0)
const videoSrc = ref('')
const videoLoading = ref(false)
const videoError = ref(false)
const imageNaturalSize = ref({ width: 800, height: 600 })

const displayAlt = computed(() => props.alt || 'Вложение')
const frameHeight = computed(() => (
  isVideo.value ? VIDEO_FRAME_HEIGHT_PX : IMAGE_FRAME_HEIGHT_PX
))

async function ensureVideoLoaded(): Promise<string> {
  if (videoSrc.value) {
    return videoSrc.value
  }
  if (!props.src || videoLoading.value) {
    return videoSrc.value
  }

  videoLoading.value = true
  videoError.value = false
  try {
    const url = await resolveMediaUrlForDisplay(props.src)
    videoSrc.value = url
    return url
  } catch {
    videoError.value = true
    return ''
  } finally {
    videoLoading.value = false
  }
}

async function ensureFreshImageSrc(): Promise<string> {
  const current = resolvedSrc.value
  if (current?.startsWith('blob:')) {
    try {
      if ((await fetch(current)).ok) {
        return current
      }
    } catch {
      // blob revoked после idle / LRU
    }
  } else if (current && !hasError.value) {
    return current
  }

  await reload({ force: true })
  return resolvedSrc.value
}

async function openChatMediaLightbox(startSrc: string, kind: 'image' | 'video') {
  const media = flattenChatMedia(activeMessages.value)
  const startIndex = media.findIndex(item => item.fileUrl === props.src)
  if (startIndex < 0 || media.length <= 1) {
    if (kind === 'video') {
      await openVideoPreview(startSrc)
      return
    }
    await openImageLightbox(
      startSrc,
      imageNaturalSize.value.width,
      imageNaturalSize.value.height,
    )
    return
  }

  const items = await Promise.all(media.map(async (item, index) => {
    if (index === startIndex) {
      return {
        src: startSrc,
        kind: item.kind,
        width: item.kind === 'image' ? imageNaturalSize.value.width : undefined,
        height: item.kind === 'image' ? imageNaturalSize.value.height : undefined,
      }
    }

    try {
      const src = await resolveMediaUrlForDisplay(item.fileUrl)
      return {
        src,
        kind: item.kind,
      }
    } catch {
      return {
        src: item.fileUrl,
        kind: item.kind,
      }
    }
  }))

  const resolvedItems = items.filter(item => Boolean(item.src))
  if (!resolvedItems.length) {
    return
  }

  await openMediaLightbox({
    items: resolvedItems,
    index: Math.min(startIndex, resolvedItems.length - 1),
  })
}

const handleGalleryClick = async (event: MouseEvent) => {
  event.preventDefault()
  event.stopPropagation()

  if (isVideo.value) {
    const src = await ensureVideoLoaded()
    if (src) {
      await openChatMediaLightbox(src, 'video')
    }
    return
  }

  const src = await ensureFreshImageSrc()
  if (!src || hasError.value) {
    return
  }

  await openChatMediaLightbox(src, 'image')
}

const handleMediaError = () => {
  if (mediaReloadAttempts.value >= 1) {
    return
  }

  mediaReloadAttempts.value += 1
  void reload({ force: true })
}

const handleImageLoad = (event: Event) => {
  setImageDimensions(event)
  const img = event.target as HTMLImageElement
  if (img?.naturalWidth && img?.naturalHeight) {
    imageNaturalSize.value = {
      width: img.naturalWidth,
      height: img.naturalHeight,
    }
  }
}

watch(
  () => [props.src, props.poster] as const,
  () => {
    mediaReloadAttempts.value = 0
    videoSrc.value = ''
    videoError.value = false
    videoLoading.value = false
    imageNaturalSize.value = { width: 800, height: 600 }
  },
)
</script>

<template>
  <div
    class="matrix-media-frame"
    :class="{
      'matrix-media-frame--video': isVideo,
      'matrix-media-frame--loading': isVideo ? videoLoading : isLoading,
      'matrix-media-frame--error': isVideo ? videoError : hasError,
      'matrix-media-frame--has-poster': isVideo && Boolean(posterSrc),
    }"
    :style="{ height: `${frameHeight}px` }"
  >
    <template v-if="isVideo">
      <button
        type="button"
        class="attachment-video-lazy"
        :disabled="videoLoading"
        @click="handleGalleryClick"
      >
        <span
          v-if="videoLoading"
          class="matrix-media-placeholder matrix-media-placeholder--on-dark"
        >
          Загрузка...
        </span>
        <span
          v-else-if="videoError"
          class="matrix-media-placeholder matrix-media-placeholder--error"
        >
          Не удалось загрузить видео
        </span>
        <template v-else>
          <img
            v-if="posterSrc"
            :src="posterSrc"
            :alt="displayAlt"
            class="attachment-video-poster-img"
            draggable="false"
          >
          <span
            v-else
            class="attachment-video-poster"
            :class="{ 'attachment-video-poster--loading': posterLoading && !fallbackPosterError }"
          />
          <span
            class="attachment-video-play"
            aria-hidden="true"
          >
            <VIcon
              icon="ri-play-fill"
              size="28"
            />
          </span>
        </template>
      </button>
    </template>

    <template v-else>
      <div
        v-if="isLoading"
        class="matrix-media-placeholder"
      >
        Загрузка...
      </div>

      <div
        v-else-if="hasError"
        class="matrix-media-placeholder matrix-media-placeholder--error"
      >
        Не удалось загрузить медиа
      </div>

      <a
        v-else-if="resolvedSrc"
        :href="resolvedSrc"
        :data-pswp-src="resolvedSrc"
        :data-pswp-width="imageNaturalSize.width"
        :data-pswp-height="imageNaturalSize.height"
        class="pswp-gallery"
        @click="handleGalleryClick"
      >
        <img
          :src="resolvedSrc"
          :alt="displayAlt"
          class="attachment-image"
          @load="handleImageLoad"
          @error="handleMediaError"
        >
      </a>
    </template>
  </div>
</template>

<style scoped lang="scss">
.matrix-media-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  max-width: 100%;
  min-width: 140px;
  overflow: hidden;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.06);

  &--video {
    min-width: 180px;
    width: min(100%, 240px);
    background: rgba(0, 0, 0, 0.85);
  }

  &--video.matrix-media-frame--has-poster {
    background: #111;
  }

  &--loading,
  &--error {
    width: min(100%, 240px);
  }
}

.matrix-media-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 12px;
  color: rgba(0, 0, 0, 0.55);
  font-size: 12px;
  text-align: center;
}

.matrix-media-placeholder--on-dark {
  color: rgba(255, 255, 255, 0.75);
}

.matrix-media-placeholder--error {
  color: rgb(var(--v-theme-error));
}

.pswp-gallery {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  max-width: 100%;
}

.attachment-image {
  display: block;
  height: 100%;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
}

.attachment-video-lazy {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  color: inherit;
}

.attachment-video-poster {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background:
    radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.08), transparent 45%),
    rgba(0, 0, 0, 0.85);

  &--loading {
    background:
      linear-gradient(90deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.04)),
      rgba(0, 0, 0, 0.85);
    background-size: 200% 100%;
    animation: poster-shimmer 1.2s ease-in-out infinite;
  }
}

.attachment-video-poster-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.attachment-video-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.28);
  border-radius: 8px;
  pointer-events: none;
}

@keyframes poster-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
</style>
